import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Banner, Button, EmptyState, Panel, Spinner, StatTile } from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/i18n";
import type { Lang } from "@/i18n";
import type { Translations } from "@/i18n/en";
import { formatDate as formatDateIntl, formatDateTime as formatDateTimeIntl, formatNumber } from "@/lib/format";
import { getProject, getSurveyTiming, listWards, listZones } from "@/lib/api";
import type { SurveyTimingFilter } from "@/lib/api";
import type {
  BlockTimingSummary,
  Project,
  SurveyorTimingSummary,
  SurveyTimingReport,
  Ward,
  WardTimingSummary,
  Zone,
} from "@/lib/types";

// --- helpers ----------------------------------------------------------------

type TT = Translations["surveyTimingPage"];

function formatDuration(seconds: number | null, tt: TT): string {
  if (seconds === null) return "—";
  if (seconds < 60) return tt.durationSeconds(Math.round(seconds));
  const mins = Math.round(seconds / 60);
  if (mins < 60) return tt.durationMinutes(mins);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? tt.durationHoursMinutes(h, m) : tt.durationHours(h);
}

function formatDatetime(iso: string | null, lang: Lang): string {
  if (!iso) return "—";
  return formatDateTimeIntl(iso, lang, { dateStyle: "short", timeStyle: "short" });
}

function formatDate(iso: string | null, lang: Lang): string {
  if (!iso) return "—";
  return formatDateIntl(iso, lang, { dateStyle: "medium" });
}

// --- table helpers ----------------------------------------------------------

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-3 py-2 text-2xs font-semibold uppercase tracking-wider text-ink-muted ${right ? "text-right" : "text-left"}`}
    >
      {children}
    </th>
  );
}

function Td({ children, right, muted }: { children: React.ReactNode; right?: boolean; muted?: boolean }) {
  return (
    <td
      className={`px-3 py-2 text-xs ${right ? "text-right tabular-nums" : ""} ${muted ? "text-ink-muted" : "text-ink"}`}
    >
      {children}
    </td>
  );
}

function TableShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

// --- By-surveyor table ------------------------------------------------------

function BySurveyorTable({ rows }: { rows: SurveyorTimingSummary[] }) {
  const { t, lang } = useLanguage();
  const tt = t.surveyTimingPage;
  if (rows.length === 0) {
    return <EmptyState title={tt.noSurveysTitle} body={tt.noSurveysBody} />;
  }
  return (
    <TableShell>
      <thead className="border-b border-line bg-surface-sunken">
        <tr>
          <Th>{tt.colSurveyor}</Th>
          <Th right>{tt.colPropertiesSurveyed}</Th>
          <Th>{tt.colFirstSurvey}</Th>
          <Th>{tt.colLastSurvey}</Th>
          <Th right>{tt.colAvgPropertyDuration}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {rows.map((r) => (
          <tr key={r.username} className="hover:bg-surface-sunken">
            <Td>{r.username}</Td>
            <Td right>{formatNumber(r.properties_surveyed, lang)}</Td>
            <Td muted>{formatDate(r.first_survey, lang)}</Td>
            <Td muted>{formatDate(r.last_survey, lang)}</Td>
            <Td right>{formatDuration(r.avg_property_duration_seconds, tt)}</Td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

// --- By-block table ---------------------------------------------------------

function ByBlockTable({ rows }: { rows: BlockTimingSummary[] }) {
  const { t, lang } = useLanguage();
  const tt = t.surveyTimingPage;
  const statusLabel: Record<string, string> = {
    pending: tt.statusPending,
    in_progress: tt.statusInProgress,
    submitted: tt.statusSubmitted,
  };
  if (rows.length === 0) {
    return <EmptyState title={tt.noBlocksTitle} body={tt.noBlocksBody} />;
  }
  return (
    <TableShell>
      <thead className="border-b border-line bg-surface-sunken">
        <tr>
          <Th>{tt.colBlock}</Th>
          <Th>{tt.colWard}</Th>
          <Th>{tt.colSurveyor}</Th>
          <Th>{tt.colStatus}</Th>
          <Th>{tt.colClaimed}</Th>
          <Th>{tt.colSubmitted}</Th>
          <Th right>{tt.colBlockDuration}</Th>
          <Th right>{tt.colAvgPerProperty}</Th>
          <Th right>{tt.colProperties}</Th>
          <Th right>{tt.colSurveyed}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {rows.map((r) => (
          <tr key={r.block_id} className="hover:bg-surface-sunken">
            <Td>{r.block_name}</Td>
            <Td muted>{r.ward_name}</Td>
            <Td muted>{r.assigned_surveyor ?? "—"}</Td>
            <Td muted>{statusLabel[r.status] ?? r.status}</Td>
            <Td muted>{formatDatetime(r.claimed_at, lang)}</Td>
            <Td muted>{formatDatetime(r.submitted_at, lang)}</Td>
            <Td right>{formatDuration(r.duration_seconds, tt)}</Td>
            <Td right>{formatDuration(r.avg_property_duration_seconds, tt)}</Td>
            <Td right>{formatNumber(r.properties_total, lang)}</Td>
            <Td right>{formatNumber(r.properties_completed, lang)}</Td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

// --- By-ward table ----------------------------------------------------------

function ByWardTable({ rows }: { rows: WardTimingSummary[] }) {
  const { t, lang } = useLanguage();
  const tt = t.surveyTimingPage;
  if (rows.length === 0) {
    return <EmptyState title={tt.noWardsTitle} body={tt.noWardsBody} />;
  }
  return (
    <TableShell>
      <thead className="border-b border-line bg-surface-sunken">
        <tr>
          <Th>{tt.colWard}</Th>
          <Th>{tt.colZone}</Th>
          <Th right>{tt.colBlocksTotal}</Th>
          <Th right>{tt.colSubmitted}</Th>
          <Th right>{tt.colPropertiesSurveyed}</Th>
          <Th>{tt.colSurveyors}</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {rows.map((r) => (
          <tr key={r.ward_id} className="hover:bg-surface-sunken">
            <Td>{r.ward_name}</Td>
            <Td muted>{r.zone_name}</Td>
            <Td right>{formatNumber(r.blocks_total, lang)}</Td>
            <Td right>{formatNumber(r.blocks_submitted, lang)}</Td>
            <Td right>{formatNumber(r.properties_completed, lang)}</Td>
            <Td muted>{r.surveyors.join(", ") || "—"}</Td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

// --- Page -------------------------------------------------------------------

const ALL = "all";

export default function SurveyTimingPage() {
  const { t, lang } = useLanguage();
  const tt = t.surveyTimingPage;
  const { projectId: projectIdParam = "" } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);

  const [project, setProject] = useState<Project | null>(null);
  const [report, setReport] = useState<SurveyTimingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [zones, setZones] = useState<Zone[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [selectedWardId, setSelectedWardId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Load project and hierarchy once.
  useEffect(() => {
    getProject(projectId).then(setProject).catch(() => {});
    listZones(projectId).then(setZones).catch(() => setZones([]));
  }, [projectId]);

  // Load wards when zone changes.
  useEffect(() => {
    if (selectedZoneId === null) {
      // Fetch all wards for the project across all zones.
      Promise.all(zones.map((z) => listWards(z.id)))
        .then((lists) => setWards(lists.flat()))
        .catch(() => setWards([]));
    } else {
      listWards(selectedZoneId).then(setWards).catch(() => setWards([]));
    }
  }, [selectedZoneId, zones]);

  // Fetch timing report.
  const fetchReport = () => {
    setLoading(true);
    setError(null);
    const filters: SurveyTimingFilter = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (selectedWardId !== null) filters.wardId = selectedWardId;
    else if (selectedZoneId !== null) filters.zoneId = selectedZoneId;
    getSurveyTiming(projectId, filters)
      .then((r) => { setReport(r); setLoading(false); })
      .catch((err) => { setError(String(err)); setLoading(false); });
  };

  useEffect(() => { fetchReport(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived stats for the tile row.
  const totalProperties = report?.by_user.reduce((s, r) => s + r.properties_surveyed, 0) ?? 0;
  const submittedBlocks = report?.by_block.filter((b) => b.status === "submitted").length ?? 0;
  const activeSurveyors = report?.by_user.length ?? 0;

  // wards is already scoped to selectedZoneId by the useEffect above.
  const visibleWards = wards;

  return (
    <AppShell
      crumbs={[
        { label: t.sidebar.projects, href: "/" },
        { label: project?.name ?? "…", href: `/projects/${projectId}` },
        { label: tt.title },
      ]}
    >
      <div className="flex flex-col gap-3 p-4">
        {error && (
          <Banner tone="danger" action={<Button size="sm" onClick={() => setError(null)}>{t.common.dismiss}</Button>}>
            {error}
          </Banner>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{tt.filterFrom}</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-8 rounded-md border border-line bg-surface-sunken px-2 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{tt.filterTo}</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-8 rounded-md border border-line bg-surface-sunken px-2 text-xs text-ink focus:outline-none focus:ring-1 focus:ring-accent-solid"
            />
          </div>
          {zones.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{tt.filterZone}</span>
              <Select
                value={selectedZoneId === null ? ALL : String(selectedZoneId)}
                onValueChange={(v) => {
                  setSelectedZoneId(v === ALL ? null : Number(v));
                  setSelectedWardId(null);
                }}
              >
                <SelectTrigger className="h-8 w-36 border-line bg-surface-sunken text-xs">
                  <SelectValue placeholder={tt.allZones} />
                </SelectTrigger>
                <SelectContent side="bottom" avoidCollisions={false} position="popper">
                  <SelectItem value={ALL}>{tt.allZones}</SelectItem>
                  {zones.map((z) => (
                    <SelectItem key={z.id} value={String(z.id)}>{z.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {visibleWards.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-semibold uppercase tracking-wider text-ink-muted">{tt.filterWard}</span>
              <Select
                value={selectedWardId === null ? ALL : String(selectedWardId)}
                onValueChange={(v) => setSelectedWardId(v === ALL ? null : Number(v))}
              >
                <SelectTrigger className="h-8 w-36 border-line bg-surface-sunken text-xs">
                  <SelectValue placeholder={tt.allWards} />
                </SelectTrigger>
                <SelectContent side="bottom" avoidCollisions={false} position="popper">
                  <SelectItem value={ALL}>{tt.allWards}</SelectItem>
                  {visibleWards.map((w) => (
                    <SelectItem key={w.id} value={String(w.id)}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button size="sm" variant="primary" onClick={fetchReport}>
            {tt.apply}
          </Button>
          {(fromDate || toDate || selectedZoneId !== null || selectedWardId !== null) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setSelectedZoneId(null);
                setSelectedWardId(null);
              }}
            >
              {tt.clear}
            </Button>
          )}
        </div>

        {/* Summary stat tiles */}
        <div className="grid grid-cols-3 gap-3">
          <StatTile label={tt.statPropertiesSurveyed} value={formatNumber(totalProperties, lang)} tone="good" />
          <StatTile label={tt.statBlocksSubmitted} value={formatNumber(submittedBlocks, lang)} tone="accent" />
          <StatTile label={tt.statActiveSurveyors} value={formatNumber(activeSurveyors, lang)} tone="neutral" />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : report === null ? null : (
          <Tabs defaultValue="by_block">
            <TabsList>
              <TabsTrigger value="by_user">{tt.tabByUser}</TabsTrigger>
              <TabsTrigger value="by_block">{tt.tabByBlock}</TabsTrigger>
              <TabsTrigger value="by_ward">{tt.tabByWard}</TabsTrigger>
            </TabsList>

            <TabsContent value="by_user">
              <Panel title={tt.panelByUser(formatNumber(report.by_user.length, lang))}>
                <BySurveyorTable rows={report.by_user} />
              </Panel>
            </TabsContent>

            <TabsContent value="by_block">
              <Panel title={tt.panelByBlock(formatNumber(report.by_block.length, lang))}>
                <ByBlockTable rows={report.by_block} />
              </Panel>
            </TabsContent>

            <TabsContent value="by_ward">
              <Panel title={tt.panelByWard(formatNumber(report.by_ward.length, lang))}>
                <ByWardTable rows={report.by_ward} />
              </Panel>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppShell>
  );
}
